import { useState, useEffect, useCallback, useMemo } from 'react';
import { getTrip, signinWithZzimToken } from '@/utils/actions';
import { SigninResultCGType, SurveyCGType } from '@/types/graphql/root';
import useActionHandler from '@/app/hooks/useActionHandler';
import { useSetAtom } from 'jotai';
import { globalTripAtom, globalTripIdAtom } from '@/atom/trip';

export default function useTripData(
  tripId: string | null,
  initTrip: SurveyCGType | null,
  initIsLoggedin: boolean,
  initZzimcarProductsProps: {
    zzimRentCarZone: string | null;
    zzimRentCarZoneName: string | null;
    startDate: any;
    endDate: any;
  },
) {
  const [trip, setTrip] = useState<SurveyCGType | null>(initTrip);
  const [isLoggedin, setIsLoggedin] = useState(initIsLoggedin);
  const [signinResult, setSigninResult] = useState<SigninResultCGType | null>(null);

  const setGlobalTrip = useSetAtom(globalTripAtom);
  const setGlobalTripId = useSetAtom(globalTripIdAtom);

  const { execute: loadTrip, data: tripDataFromHook } = useActionHandler({
    action: getTrip,
    key: tripId as string,
  });

  useEffect(() => {
    setGlobalTripId(tripId ?? null);
  }, [tripId, setGlobalTripId]);

  useEffect(() => {
    setGlobalTrip(trip);
  }, [trip, setGlobalTrip]);

  useEffect(() => {
    if (tripDataFromHook && JSON.stringify(tripDataFromHook) !== JSON.stringify(trip)) {
      setTrip(tripDataFromHook);
    }
  }, [tripDataFromHook]);

  useEffect(() => {
    signinWithZzimToken().then((result) => {
      if (!result?.accessToken) {
        setIsLoggedin(false);
      } else {
        setIsLoggedin(true);
        setSigninResult(result ?? null);
      }
    });
  }, []);

  const refetchTrip = useCallback(async () => {
    if (!tripId) return;
    await loadTrip(tripId);
  }, [tripId, loadTrip]);

  const zzimcarProductsProps = useMemo(() => {
    return {
      zzimRentCarZone: initZzimcarProductsProps.zzimRentCarZone,
      zzimRentCarZoneName: initZzimcarProductsProps.zzimRentCarZoneName,
      startDate: trip?.fromDateTime,
      endDate: trip?.toDateTime,
    };
  }, [initZzimcarProductsProps, trip]);

  return {
    trip,
    setTrip,
    isLoggedin,
    signinResult,
    refetchTrip,
    zzimcarProductsProps,
  };
}
